import { DEFAULT_MEASUREMENT_SYSTEM, isMeasurementSystem, MEASUREMENT_PROFILES, type MeasurementProfile, type MeasurementSystem } from './measurementSystem'

export type MeasurementQuantity = keyof MeasurementProfile
const UNITS = {
  mm: ['length', 1], cm: ['length', 10], m: ['length', 1000], in: ['length', 25.4], ft: ['length', 304.8],
  kg: ['mass', 1], g: ['mass', 0.001], t: ['mass', 1000], lb: ['mass', 0.45359237],
  mm3: ['volume', 1], cm3: ['volume', 1000], m3: ['volume', 1e9], ft3: ['volume', 28316846.592], in3: ['volume', 16387.064], l: ['volume', 1e6],
  mm2: ['area', 1e-6], cm2: ['area', 1e-4], m2: ['area', 1], ft2: ['area', 0.09290304], in2: ['area', 0.00064516],
  'kg/m2': ['surfaceLoad', 1], 'lb/ft2': ['surfaceLoad', 0.45359237 / 0.09290304],
} as const
export type MeasurementUnit = keyof typeof UNITS

/** Decode source metadata; the caller must supply an explicit legacy fallback for absent units. */
export function parseMeasurementUnit(value: unknown): MeasurementUnit {
  const aliases: Record<string, MeasurementUnit> = {
    millimeter: 'mm', millimeters: 'mm', millimetre: 'mm', millimetres: 'mm',
    centimeter: 'cm', centimeters: 'cm', centimetre: 'cm', centimetres: 'cm',
    meter: 'm', meters: 'm', metre: 'm', metres: 'm', inch: 'in', inches: 'in', foot: 'ft', feet: 'ft',
    kilogram: 'kg', kilograms: 'kg', pound: 'lb', pounds: 'lb', lbs: 'lb', ton: 't', tonne: 't', tonnes: 't',
  }
  const code = typeof value === 'string' ? value.trim().toLowerCase().replace(/²/g, '2').replace(/³/g, '3') : ''
  const canonical = Object.prototype.hasOwnProperty.call(aliases, code) ? aliases[code] : code
  if (!Object.prototype.hasOwnProperty.call(UNITS, canonical)) throw new Error('Unité de mesure inconnue')
  return canonical as MeasurementUnit
}

export function resolveMeasurementSystem(value: unknown): MeasurementSystem {
  if (value == null) return DEFAULT_MEASUREMENT_SYSTEM
  if (!isMeasurementSystem(value)) throw new Error('Système de mesure inconnu')
  return value
}

export function measurementUnit(system: MeasurementSystem, quantity: MeasurementQuantity): MeasurementUnit {
  return MEASUREMENT_PROFILES[resolveMeasurementSystem(system)][quantity]
}

export function unitSymbol(unit: MeasurementUnit): string {
  return unit.replace(/2/g, '²').replace(/3/g, '³')
}

/** Source units must be explicit, including for legacy data. No display-profile inference. */
export function convertMeasurement(value: number, from: MeasurementUnit, to: MeasurementUnit): number {
  if (!Object.prototype.hasOwnProperty.call(UNITS, from) || !Object.prototype.hasOwnProperty.call(UNITS, to)) throw new Error('Unité de mesure inconnue')
  if (UNITS[from][0] !== UNITS[to][0]) throw new Error('Unités de mesure incompatibles')
  if (!Number.isFinite(value)) throw new Error('La mesure doit être un nombre fini')
  const result = from === to ? value : value * (UNITS[from][1] / UNITS[to][1])
  if (!Number.isFinite(result)) throw new Error('La mesure dépasse la plage numérique')
  return result
}

const DECIMALS: Partial<Record<MeasurementUnit, number>> = { mm: 1, cm: 2, in: 2, kg: 2, lb: 2 }
export interface MeasurementFormatOptions {
  locale?: string
  maximumFractionDigits?: number
  nullText?: string
  includeUnit?: boolean
}

export function formatMeasurement(value: number | null | undefined, from: MeasurementUnit, to: MeasurementUnit, options: MeasurementFormatOptions = {}): string {
  if (value == null) return options.nullText ?? '—'
  const number = convertMeasurement(value, from, to)
  const precision = options.maximumFractionDigits ?? DECIMALS[to] ?? 3
  const formatter = new Intl.NumberFormat(options.locale ?? 'fr-FR', { maximumFractionDigits: precision })
  const threshold = 10 ** -precision
  let text = formatter.format(number)
  // A nonzero physical measure must not be presented as zero.
  if (number !== 0 && Math.abs(number) < threshold / 2) {
    text = number > 0 ? `< ${formatter.format(threshold)}` : `> ${formatter.format(-threshold)}`
  }
  return options.includeUnit === false ? text : `${text} ${unitSymbol(to)}`
}

/** Readable editing text (15 significant digits), without grouping.
 * The draft retains the full source number separately; this text is never saved unless edited.
 */
export function editableMeasurement(value: number | null, from: MeasurementUnit, to: MeasurementUnit, locale = 'fr-FR'): string {
  if (value == null) return ''
  const number = convertMeasurement(value, from, to)
  const decimal = new Intl.NumberFormat(locale).formatToParts(1.1).find(part => part.type === 'decimal')?.value ?? '.'
  return String(Number(number.toPrecision(15))).replace('.', decimal)
}

export type ParsedMeasurement =
  | { status: 'valid'; value: number }
  | { status: 'empty' | 'incomplete' | 'invalid'; reason?: string }

/** Decimal comma/dot accepted; grouping is deliberately excluded from entry fields. */
export function parseMeasurementText(text: string, locale = 'fr-FR'): ParsedMeasurement {
  const trimmed = text.trim()
  if (!trimmed) return { status: 'empty' }
  if (/^[+-]?$/.test(trimmed) || /^[+-]?\d*[.,]$/.test(trimmed) || /^[+-]?(?:\d+(?:[.,]\d*)?|[.,]\d+)[eE][+-]?$/.test(trimmed)) return { status: 'incomplete' }
  const group = new Intl.NumberFormat(locale).formatToParts(12345.6).find(part => part.type === 'group')?.value
  const separator = trimmed.includes(',') ? ',' : '.'
  if (separator === group && /^[+-]?[1-9]\d{0,2}[.,]\d{3}$/.test(trimmed)) {
    return { status: 'invalid', reason: 'Saisie ambiguë : utilisez le séparateur décimal local, sans séparateur de milliers.' }
  }
  if (!/^[+-]?(?:\d+(?:[.,]\d+)?|[.,]\d+)(?:[eE][+-]?\d+)?$/.test(trimmed)) return { status: 'invalid', reason: 'Saisissez un nombre sans séparateur de milliers.' }
  const value = Number(trimmed.replace(',', '.'))
  return Number.isFinite(value) ? { status: 'valid', value } : { status: 'invalid', reason: 'La mesure doit être un nombre fini.' }
}
